import { useEffect, useState } from "react";
import { ArrowLeft, ShoppingCart, Building2, LogOut } from "lucide-react";
import { fetchHotelPlans, validateHotel, startAddonCheckout, clearHotelSession } from "./hotelClient";

// Shown when Hotel Mode is active AND the user taps "Get Chat Credits".
// Displays: (a) their current credit balance banner, (b) the 3 memberships
// (informational — actual purchase happens on /hotel/signup form), and
// (c) the 3 add-on packs which top up the shared credit balance.
//
// Add-ons are only enabled for hotels with an active membership — the button
// is disabled otherwise and shows a small helper line.
export default function HotelCredits({ onBack, onGoSignup }) {
  const [hotel, setHotel] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([validateHotel(), fetchHotelPlans()])
      .then(([h, p]) => { setHotel(h); setPlans(p); })
      .finally(() => setLoading(false));
  }, []);

  const canBuyAddons = !!hotel && hotel.status === "active";

  const handleAddon = async (priceId) => {
    setErr("");
    setBusy(priceId);
    try {
      const { url } = await startAddonCheckout(priceId);
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start add-on checkout.");
      setBusy("");
    }
  };

  const handleSignOut = () => {
    clearHotelSession();
    onBack && onBack();
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="hotel-credits-back">
            <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-[#4A4A4A]">Hotel Membership &amp; Chat Credits</h1>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-10">
        {loading && <p className="text-center text-sm text-[#9A9A9A]">Loading hotel details…</p>}

        {!loading && !hotel && (
          <div className="bg-white rounded-2xl border-2 border-[#E0E0E0] p-6 text-center" data-testid="hotel-credits-no-hotel">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-[#B89823]" />
            <p className="text-lg font-semibold text-[#4A4A4A] mb-2">No active Hotel Mode on this device</p>
            <p className="text-sm text-[#6B6B6B] mb-4">Activate your hotel with an <span className="font-mono">HTL-XXXX-XXXX</span> code, or buy a hotel membership.</p>
            <button onClick={onGoSignup} data-testid="hotel-credits-go-signup" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8D4A8] text-[#5A4A32] font-semibold text-sm hover:bg-[#D9C599] transition-all">
              <ShoppingCart className="w-4 h-4" /> Buy hotel membership
            </button>
          </div>
        )}

        {!loading && hotel && (
          <>
            {/* Status banner */}
            <div className="bg-white rounded-2xl border-2 border-[#E8D4A8] p-5 mb-6" data-testid="hotel-credits-status-banner">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-xs font-semibold text-[#B89823] uppercase tracking-wide mb-1">Hotel Mode active</p>
                  <p className="text-xl font-bold text-[#4A4A4A]">{hotel.hotel_name}</p>
                  <p className="text-xs text-[#6B6B6B]">{hotel.membership_tier} · Renews {hotel.next_renewal_at ? new Date(hotel.next_renewal_at).toLocaleDateString() : "—"}</p>
                </div>
                <button onClick={handleSignOut} data-testid="hotel-credits-sign-out" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#FCEAEA] text-[#A55B64] hover:bg-[#F5D4D4] transition-all">
                  <LogOut className="w-3 h-3" /> Sign out this iPad
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <Stat label="Monthly allocation" value={(hotel.monthly_credit_allocation || 0).toLocaleString()} />
                <Stat label="Monthly remaining" value={(hotel.monthly_credits_remaining || 0).toLocaleString()} />
                <Stat label="Add-on balance" value={(hotel.addon_credits_remaining || 0).toLocaleString()} highlight />
                <Stat label="Total available" value={(hotel.total_available || 0).toLocaleString()} highlight />
              </div>
              {(hotel.total_available || 0) === 0 && (
                <p className="mt-3 text-xs text-[#A55B64]" data-testid="hotel-credits-paused-note">
                  Typed and spoken chat are paused until renewal or an add-on purchase. Picture cards, emotions and quick messages remain free.
                </p>
              )}
            </div>

            {/* Add-on packs */}
            <h2 className="text-lg font-bold text-[#4A4A4A] mb-3">Add-on credit packs</h2>
            <p className="text-xs text-[#6B6B6B] mb-4">Top up the shared central balance. Add-on credits never expire. An active hotel membership is required to use them.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {(plans?.addons || []).map((a) => (
                <div key={a.price_id} className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-5">
                  <p className="text-center text-xs font-bold text-[#6B6B6B] uppercase tracking-wide mb-2">{a.name.replace("Hotel Add-On", "Add-On")}</p>
                  <p className="text-center text-3xl font-bold text-[#4A4A4A]">{(a.credits || 0).toLocaleString()}</p>
                  <p className="text-center text-xs text-[#6B6B6B] mb-3">credits</p>
                  <p className="text-center text-xl font-bold text-[#B89823] mb-3">£{a.price_gbp}</p>
                  <button
                    onClick={() => handleAddon(a.price_id)}
                    disabled={!canBuyAddons || !!busy}
                    data-testid={`hotel-addon-${a.credits}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] transition-all disabled:opacity-50"
                  >
                    {busy === a.price_id ? "Redirecting…" : (<><ShoppingCart className="w-4 h-4" /> Buy add-on</>)}
                  </button>
                </div>
              ))}
              {!plans && <p className="text-xs text-[#9A9A9A]">Loading add-ons…</p>}
            </div>

            {!canBuyAddons && (
              <p className="text-xs text-[#A55B64] text-center mb-4">Add-ons are available once the hotel membership is active.</p>
            )}
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center mb-4" data-testid="hotel-credits-error">{err}</div>}

            <div className="text-xs text-[#9A9A9A] text-center border-t border-[#E8E5DC] pt-4">
              Only typed &amp; spoken chat use credits. Picture cards, emotions, quick messages and all other tools remain free.
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[#FFF7D6] border border-[#E8D4A8]" : "bg-[#F3F0E6]"}`}>
      <p className="text-[10px] uppercase tracking-wide text-[#6B6B6B] mb-1">{label}</p>
      <p className={`text-lg font-bold ${highlight ? "text-[#B89823]" : "text-[#4A4A4A]"}`}>{value}</p>
    </div>
  );
}
